import { acceptHMRUpdate, defineStore } from "pinia"

import { api } from "@/logic/api"
import { getConstants } from "@/logic/constants"
import { getI18n } from "@/logic/i18n"
import { getColumnRegistryConfig } from "@/logic/columnRegistry"
import { useAccountStore } from "@/modules/account/store"
import { useAppConfigStore } from "@/store/appConfig"
import { useSnackbarsStore } from "@/store/snackbars"
import type { SharedColumnRegistry } from "../../types/columnRegistry"

export const COLUMN_VISIBILITY = getConstants().ENTRY_LIST.COLUMN_VISIBILITY
export type ColumnVisibility = (typeof COLUMN_VISIBILITY)[keyof typeof COLUMN_VISIBILITY]
export type ColumnVisibilityContext = Record<string, string | number | null | undefined>

export type ColumnPreferenceResponse = {
  columns: Record<string, ColumnVisibility>
  defaultColumns: Record<string, ColumnVisibility>
  hasChangesFromDefault: boolean
}

export type TableColumnVisibilityState = {
  resolvedColumns: Record<string, ColumnVisibility>
  defaultColumns: Record<string, ColumnVisibility>
  isLoading: boolean
  hasLoaded: boolean
  hasChangesFromDefault: boolean
}

const loadingPromises = new Map<string, Promise<void>>()

export const useColumnVisibilityStore = defineStore("column-visibility", {
  state: () => ({
    tables: {} as Record<string, TableColumnVisibilityState>,
  }),
  getters: {
    scopeKey(): string {
      return getColumnVisibilityScopeKey()
    },
    getTableState(state) {
      return (tableKey: string, context: ColumnVisibilityContext = {}): TableColumnVisibilityState => {
        return state.tables[getTableStateKey(tableKey, context)] ?? emptyTableState()
      }
    },
    getResolvedColumns() {
      return (tableKey: string, context: ColumnVisibilityContext = {}): Record<string, ColumnVisibility> => {
        return this.getTableState(tableKey, context).resolvedColumns
      }
    },
    getDefaultColumns() {
      return (tableKey: string, context: ColumnVisibilityContext = {}): Record<string, ColumnVisibility> => {
        return this.getTableState(tableKey, context).defaultColumns
      }
    },
    getVisibleColumnKeys() {
      return (tableKey: string, context: ColumnVisibilityContext = {}): string[] => {
        return visibleColumnKeys(this.getResolvedColumns(tableKey, context))
      }
    },
    isTableReady() {
      return (tableKey: string, context: ColumnVisibilityContext = {}): boolean => {
        const tableState = this.getTableState(tableKey, context)

        return tableState.hasLoaded && !tableState.isLoading
      }
    },
  },
  actions: {
    ensureTableState(tableKey: string, context: ColumnVisibilityContext = {}): TableColumnVisibilityState {
      const stateKey = getTableStateKey(tableKey, context)
      this.tables[stateKey] ??= emptyTableState()

      return this.tables[stateKey]
    },
    async loadPreferences(tableKey: string, context: ColumnVisibilityContext = {}, force = false): Promise<void> {
      const stateKey = getTableStateKey(tableKey, context)
      const tableState = this.ensureTableState(tableKey, context)

      if (tableState.hasLoaded && !force) {
        return
      }

      const activeLoad = loadingPromises.get(stateKey)
      if (activeLoad) {
        return activeLoad
      }

      tableState.isLoading = true
      const preferenceLoad = api
        .get(`/global/column-preferences/${tableKey}`, normalizeColumnVisibilityContext(context))
        .then((response: ColumnPreferenceResponse) => applyResponseToTableState(tableState, response))
        .catch((error: unknown) => {
          useSnackbarsStore().error(getI18n().t("global.columnVisibility.loadFailure"))

          throw error
        })
        .finally(() => {
          tableState.isLoading = false
          loadingPromises.delete(stateKey)
        })

      loadingPromises.set(stateKey, preferenceLoad)

      return preferenceLoad
    },
    async persistPreferences(tableKey: string, columns: Record<string, ColumnVisibility>, context: ColumnVisibilityContext = {}): Promise<ColumnPreferenceResponse> {
      const tableState = this.ensureTableState(tableKey, context)
      tableState.isLoading = true

      try {
        return (await api.patch(`/global/column-preferences/${tableKey}`, {
          columns,
          context: normalizeColumnVisibilityContext(context),
        })) as ColumnPreferenceResponse
      } finally {
        tableState.isLoading = false
      }
    },
    applyPreferenceResponse(tableKey: string, response: ColumnPreferenceResponse, context: ColumnVisibilityContext = {}): void {
      applyResponseToTableState(this.ensureTableState(tableKey, context), response)
    },
  },
})

if (import.meta.hot) {
  // @ts-ignore
  import.meta.hot.accept(acceptHMRUpdate(useColumnVisibilityStore, import.meta.hot))
}

export function normalizeColumnVisibilityContext(context: ColumnVisibilityContext): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(context)
      .filter(([, contextValue]) => contextValue !== null && contextValue !== undefined && contextValue !== "")
      .sort(([firstKey], [secondKey]) => firstKey.localeCompare(secondKey)),
  ) as Record<string, string | number>
}

export function columnVisibilityContextKey(context: ColumnVisibilityContext): string {
  return JSON.stringify(normalizeColumnVisibilityContext(context))
}

export function shouldRefreshColumnVisibilityPreferences(tableKey: string, context: ColumnVisibilityContext): boolean {
  const tableConfig = getColumnRegistryConfig()[tableKey as keyof SharedColumnRegistry]

  const hasDynamicColumns = "dynamic_columns" in tableConfig && Object.keys(tableConfig.dynamic_columns).length > 0

  return hasDynamicColumns || Object.keys(normalizeColumnVisibilityContext(context)).length > 0
}

function visibleColumnKeys(columns: Record<string, ColumnVisibility>): string[] {
  return Object.entries(columns)
    .filter(([, visibility]) => visibility !== COLUMN_VISIBILITY.HIDDEN)
    .map(([columnKey]) => columnKey)
}

function emptyTableState(): TableColumnVisibilityState {
  return {
    resolvedColumns: {},
    defaultColumns: {},
    isLoading: false,
    hasLoaded: false,
    hasChangesFromDefault: false,
  }
}

function applyResponseToTableState(tableState: TableColumnVisibilityState, response: ColumnPreferenceResponse): void {
  tableState.resolvedColumns = response.columns
  tableState.defaultColumns = response.defaultColumns
  tableState.hasChangesFromDefault = response.hasChangesFromDefault
  tableState.hasLoaded = true
}

function getColumnVisibilityScopeKey(): string {
  return `user:${useAccountStore().id ?? "none"}:app:${useAppConfigStore().appId ?? "none"}`
}

function getTableStateKey(tableKey: string, context: ColumnVisibilityContext): string {
  return `${getColumnVisibilityScopeKey()}:table:${tableKey}:context:${columnVisibilityContextKey(context)}`
}
